import { useState, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AD_TYPES } from "../../../../components/core/blocks/AdPlan/constants";
import { useParams } from "@tanstack/react-router";
import { useDates } from "../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import AdTypeChart from "./AdTypeChart";
import AdTypeCards from "./AdTypeCards";
import Loading from "../../../../components/core/blocks/Loading";
import useAdTypeData from "../../hooks/useAdTypeData";
import { ButtonGroup } from "../../../../components/core/basic/ButtonGroup";

export const adTypesByChannel = {
  amazon: [
    AD_TYPES.SPONSORED_PRODUCT,
    AD_TYPES.SPONSORED_PRODUCT_AUTO,
    AD_TYPES.SPONSORED_BRAND,
    AD_TYPES.SPONSORED_BRAND_V2,
    AD_TYPES.SPONSORED_BRAND_VIDEO,
    AD_TYPES.SPONSORED_DISPLAY,
    AD_TYPES.SPONSORED_DISPLAY_V2,
  ],
  walmart: [
    AD_TYPES.SPONSORED_PRODUCT_WALMART,
    AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART,
    AD_TYPES.SPONSORED_BRAND_WALMART,
    AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART,
  ],
};

const CHART_METRIC_OPTIONS = [
  {
    value: "ad_sales",
    label: ($) => $.advertising["Ad Sales"],
    format: "currency",
  },
  {
    value: "cost",
    label: ($) => $.advertising["Ad Spend"],
    format: "currency",
  },
  {
    value: "active_ad_count",
    label: ($) => $.advertising["# of Active Plans"],
    format: "number",
  },
  {
    value: "clicks",
    label: ($) => $.advertising["Clicks"],
    format: "number",
  },
  {
    value: "impressions",
    label: ($) => $.advertising["Impressions"],
    format: "number",
  },
];

const AdTypeSection = ({ adTypeFilter, setAdTypeFilter, additionalFilters, search }) => {
  const [pieChartMetric, setPieChartMetric] = useState(CHART_METRIC_OPTIONS[0]);
  const { t } = useTranslation(["advertising"]);

  const { start, end } = useDates();
  const longDate = useDateFormatter("long");
  const { id: categoryId } = useParams({ strict: false });
  const { data: adTypeData, isLoading } = useAdTypeData(categoryId, true, additionalFilters, search);

  const pieChartData = useMemo(() => {
    return (
      adTypeData
        ?.map((type) => {
          return {
            ad_type: type.ad_type,
            color: type.color,
            y: type[pieChartMetric.value],
          };
        })
        ?.filter((type) => type.y) ?? []
    );
  }, [adTypeData, pieChartMetric]);

  if (isLoading) {
    return <Loading fullPage={false} height={500} />;
  }

  return (
    <Row>
      {!adTypeData?.length ?
        <Col className="text-center ">
          <h4>{t(($) => $.advertising["No Data Available"])}</h4>
          <p>
            {t(
              ($) =>
                $.advertising["This section will be populated once the first media plan is created on this account."],
            )}
          </p>
        </Col>
      : <>
          <Col xs={{ span: 12, order: 2 }} xl={{ span: 6, order: 1 }}>
            <div className="pb-5 d-flex justify-content-between">
              <div>
                <h3
                  style={{
                    marginTop: "0rem",
                    marginBottom: "0.2rem",
                  }}
                >
                  {t(($) => $.advertising["{label} By Media Plan Type"], { label: t(pieChartMetric.label) })}
                </h3>
                <small>
                  {longDate.format(start.toDate())}
                  {" - "}
                  {longDate.format(end.toDate())}
                </small>
              </div>
            </div>

            <div className="py-5">
              <AdTypeChart data={pieChartData} format={pieChartMetric.format} />
            </div>
            <div className="d-flex justify-content-center align-items-center pt-5 mt-5">
              <ButtonGroup
                buttons={CHART_METRIC_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onSelect={(value) => {
                  const updatedMetricSpec = CHART_METRIC_OPTIONS.find((o) => o.value === value);
                  setPieChartMetric(updatedMetricSpec);
                }}
                value={pieChartMetric.value}
                colorPalette={"buttonGroup.fuchsia"}
              />
            </div>
          </Col>
          <Col xs={{ span: 12, order: 1 }} xl={{ span: 6, order: 2 }}>
            <AdTypeCards selectedCard={adTypeFilter} setSelectedCard={setAdTypeFilter} data={adTypeData} />
          </Col>
        </>
      }
    </Row>
  );
};

export default AdTypeSection;
